/** Banners list + edit (single page). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

const BANNER_TYPES = ["hero", "inline", "category", "promo"] as const;
const ACTION_TYPES = ["none", "category", "product", "url", "promo_code"] as const;

interface Banner {
  id: number;
  type: string;
  title_ru: string | null;
  image_url: string;
  action: string;
  action_payload: any;
  sort_order: number;
  is_active: boolean;
}

export function BannersListPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const { data: banners, isLoading } = useQuery({
    queryKey: ["admin-banners"],
    queryFn: async () => {
      const { data } = await api.get("/banners");
      return data as Banner[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/banners/${id}`);
    },
    onSuccess: () => {
      toast.success(t("common.toast.deleted"));
      queryClient.invalidateQueries({ queryKey: ["admin-banners"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить баннер?",
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      await api.patch(`/banners/${id}`, { is_active });
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-banners"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? t("common.toast.errorOccurred")),
  });
  const togglingId = toggleMutation.isPending
    ? (toggleMutation.variables?.id ?? null)
    : null;

  if (isLoading) return <Skeleton className="h-96" />;

  const editingBanner =
    typeof editingId === "number"
      ? banners?.find((b) => b.id === editingId)
      : undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading">{t("nav.banners")}</h1>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          {t("common.create")}
        </Button>
      </div>

      <BannerDialog
        open={editingId !== null}
        onClose={() => setEditingId(null)}
        banner={editingBanner}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {banners?.map((b) => (
          <Card key={b.id}>
            <div className="aspect-video relative">
              <img
                src={b.image_url}
                alt={b.title_ru ?? ""}
                className="w-full h-full object-cover rounded-t-lg"
              />
              <Badge className="absolute top-2 left-2">{b.type}</Badge>
            </div>
            <CardContent className="pt-4 space-y-2">
              <h3 className="font-medium">{b.title_ru ?? <em>Без названия</em>}</h3>
              <div className="text-xs text-muted-foreground">
                Action: {b.action} {b.action_payload && JSON.stringify(b.action_payload)}
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={b.is_active}
                    disabled={togglingId === b.id}
                    onCheckedChange={(is_active) =>
                      toggleMutation.mutate({ id: b.id, is_active })
                    }
                  />
                  {togglingId === b.id ? (
                    <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {b.is_active ? "Active" : "Off"}
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId(b.id)}
                  >
                    Редактировать
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(b.id, b.title_ru ?? `#${b.id}`)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {deleteDialog}
    </div>
  );
}

function BannerDialog({
  open,
  onClose,
  banner,
}: {
  open: boolean;
  onClose: () => void;
  banner?: Banner;
}): JSX.Element {
  const mode = banner ? "edit" : "create";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Новый баннер" : "Редактировать баннер"}
          </DialogTitle>
        </DialogHeader>
        {open && (
          <BannerForm
            key={banner?.id ?? "new"}
            banner={banner}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function BannerForm({
  banner,
  onClose,
}: {
  banner?: Banner;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const mode = banner ? "edit" : "create";
  const [form, setForm] = useState({
    type: (banner?.type ?? "hero") as (typeof BANNER_TYPES)[number],
    title_ru: banner?.title_ru ?? "",
    image_url: banner?.image_url ?? "",
    action: (banner?.action ?? "none") as (typeof ACTION_TYPES)[number],
    action_payload: (banner?.action_payload ?? {}) as any,
    is_active: banner?.is_active ?? true,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (mode === "create") {
        await api.post("/banners", form);
      } else if (banner) {
        await api.patch(`/banners/${banner.id}`, form);
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? t("banners.created") : t("common.toast.saved"));
      queryClient.invalidateQueries({ queryKey: ["admin-banners"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? t("common.toast.errorOccurred")),
  });

  return (
    <div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Тип</Label>
            <Select
              value={form.type}
              onValueChange={(type: any) => setForm({ ...form, type })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BANNER_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Action</Label>
            <Select
              value={form.action}
              onValueChange={(action: any) => setForm({ ...form, action })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPES.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Заголовок (RU)</Label>
          <Input
            value={form.title_ru}
            onChange={(e) => setForm({ ...form, title_ru: e.target.value })}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Image URL</Label>
          <Input
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            placeholder="https://cdn.example.uz/banners/hero1.jpg"
          />
          <p className="text-xs text-muted-foreground">
            (Image upload в Phase 4 — пока вставь URL)
          </p>
        </div>

        {form.action === "category" && (
          <div className="space-y-1.5">
            <Label>Category ID</Label>
            <Input
              type="number"
              onChange={(e) =>
                setForm({
                  ...form,
                  action_payload: { category_id: parseInt(e.target.value, 10) },
                })
              }
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <Switch
            checked={form.is_active}
            onCheckedChange={(is_active) => setForm({ ...form, is_active })}
          />
          <Label>Активен</Label>
        </div>

      </div>
      <DialogFooter className="pt-4">
        <Button variant="ghost" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!form.image_url || saveMutation.isPending}
        >
          {mode === "create" ? t("common.create") : t("common.save")}
        </Button>
      </DialogFooter>
    </div>
  );
}
